import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Clock, CheckCircle, Loader, Plus, Edit, Trash2, FileText, Package, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatDate } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const OrderNotificationToFarm = () => {
  const [notifications, setNotifications] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    distributor_id: '',
    distributor_name: '',
    pickup_date: new Date().toISOString().split('T')[0],
    pickup_time: '',
    xlarge_crates: '0',
    large_crates: '0',
    medium_crates: '0',
    small_crates: '0',
    crack_crates: '0',
    notes: ''
  });

  useEffect(() => {
    fetchNotifications();
    fetchCustomers();
    fetchDistributors();
  }, []);

  // Get user from localStorage
  const storedUser = localStorage.getItem('user');
  const user = storedUser ? JSON.parse(storedUser) : {};
  const userRole = user.role || '';
  const canAddOrderNotification = user.can_add_order_notification || false;

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/order-notifications`, getAuthHeader());
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to fetch order notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, getAuthHeader());
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchDistributors = async () => {
    try {
      const response = await axios.get(`${API}/distributors-and-vets`, getAuthHeader());
      setDistributors(response.data.distributors || []);
    } catch (error) {
      console.error('Failed to fetch distributors');
    }
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setFormData({
        ...formData,
        customer_id: customerId,
        customer_name: customer.name,
        // Auto-fill distributor from customer's affiliated distributor
        distributor_id: customer.affiliated_distributor_id || '',
        distributor_name: customer.affiliated_distributor_name || ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name.trim()) {
      toast.error('Please select a customer');
      return;
    }

    const submitData = {
      ...formData,
      large_crates: parseInt(formData.large_crates) || 0,
      medium_crates: parseInt(formData.medium_crates) || 0,
      small_crates: parseInt(formData.small_crates) || 0,
      crack_crates: parseInt(formData.crack_crates) || 0
    };

    try {
      if (editingNotification) {
        await axios.put(`${API}/order-notifications/${editingNotification.id}`, submitData, getAuthHeader());
        toast.success('Order notification updated');
      } else {
        await axios.post(`${API}/order-notifications`, submitData, getAuthHeader());
        toast.success('Order notification created');
      }
      fetchNotifications();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (notification) => {
    setEditingNotification(notification);
    // Calculate crates from eggs if coming from approved request
    const xlargeCrates = notification.xlarge_crates || Math.round((notification.xlarge_eggs || 0) / 30);
    const largeCrates = notification.large_crates || Math.round((notification.large_eggs || 0) / 30);
    const mediumCrates = notification.medium_crates || Math.round((notification.medium_eggs || 0) / 30);
    const smallCrates = notification.small_crates || Math.round((notification.small_eggs || 0) / 30);
    const crackCrates = notification.crack_crates || Math.round((notification.cracked_eggs || 0) / 30);
    
    setFormData({
      customer_id: notification.customer_id || '',
      customer_name: notification.customer_name || '',
      distributor_id: notification.distributor_id || '',
      distributor_name: notification.distributor_name || '',
      pickup_date: notification.pickup_date || notification.expected_delivery_date || '',
      pickup_time: notification.pickup_time || '',
      xlarge_crates: xlargeCrates.toString(),
      large_crates: largeCrates.toString(),
      medium_crates: mediumCrates.toString(),
      small_crates: smallCrates.toString(),
      crack_crates: crackCrates.toString(),
      notes: notification.notes || ''
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this order notification?')) return;
    try {
      await axios.delete(`${API}/order-notifications/${id}`, getAuthHeader());
      toast.success('Order notification deleted');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingNotification(null);
    setFormData({
      customer_id: '',
      customer_name: '',
      distributor_id: '',
      distributor_name: '',
      pickup_date: new Date().toISOString().split('T')[0],
      pickup_time: '',
      xlarge_crates: '0',
      large_crates: '0',
      medium_crates: '0',
      small_crates: '0',
      crack_crates: '0',
      notes: ''
    });
  };

  const handleStatusChange = async (notificationId, newStatus) => {
    try {
      await axios.put(`${API}/order-notifications/${notificationId}/status`, 
        { status: newStatus }, 
        getAuthHeader()
      );
      toast.success(`Order marked as ${newStatus}`);
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Check if user can add/edit orders (Admin, Distributor, or Staff with permission)
  const canManageOrders = userRole === 'admin' || userRole === 'distributor' || (userRole === 'staff' && canAddOrderNotification);

  const pendingCount = notifications.filter(n => n.status === 'pending').length;
  const completedCount = notifications.filter(n => n.status === 'completed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-500">Loading order notifications...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="order-notifications-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Order Notification To Farm
          </h1>
          <p className="text-lg text-slate-600 mt-2">
            {canManageOrders ? 'View and manage customer order requests for pickup' : 'View customer order requests for pickup'}
          </p>
        </div>
        {canManageOrders && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-notification-button" className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Order
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="notification-dialog">
            <DialogHeader>
              <DialogTitle>{editingNotification ? 'Edit Order' : 'Add Order Notification'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="customer">Customer Name *</Label>
                {userRole === 'distributor' ? (
                  <Input
                    id="customer_name"
                    type="text"
                    data-testid="customer-name-input"
                    placeholder="Enter customer name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value, customer_id: '' })}
                    required
                  />
                ) : (
                  <Select onValueChange={handleCustomerChange} value={formData.customer_id}>
                    <SelectTrigger data-testid="customer-select">
                      <SelectValue placeholder="Select a customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label htmlFor="distributor">Distributor</Label>
                <Select 
                  onValueChange={(value) => {
                    if (value === 'direct') {
                      setFormData({ 
                        ...formData, 
                        distributor_id: '',
                        distributor_name: ''
                      });
                    } else {
                      const dist = distributors.find(d => d.id === value);
                      setFormData({ 
                        ...formData, 
                        distributor_id: value,
                        distributor_name: dist?.name || ''
                      });
                    }
                  }} 
                  value={formData.distributor_id || 'direct'}
                >
                  <SelectTrigger data-testid="distributor-select">
                    <SelectValue placeholder="Select a distributor (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct to Farm</SelectItem>
                    {distributors.map((dist) => (
                      <SelectItem key={dist.id} value={dist.id}>
                        {dist.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pickup_date">Expected Pickup Date *</Label>
                  <Input
                    id="pickup_date"
                    type="date"
                    data-testid="pickup-date-input"
                    value={formData.pickup_date}
                    onChange={(e) => setFormData({ ...formData, pickup_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="pickup_time">Expected Pickup Time</Label>
                  <Input
                    id="pickup_time"
                    type="time"
                    data-testid="pickup-time-input"
                    value={formData.pickup_time}
                    onChange={(e) => setFormData({ ...formData, pickup_time: e.target.value })}
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm mb-3">Order Quantities (Crates - 30 eggs each)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="xlarge_crates">X-Large (Crates)</Label>
                    <Input
                      id="xlarge_crates"
                      type="number"
                      data-testid="xlarge-crates-input"
                      value={formData.xlarge_crates}
                      onChange={(e) => setFormData({ ...formData, xlarge_crates: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="large_crates">Large (Crates)</Label>
                    <Input
                      id="large_crates"
                      type="number"
                      data-testid="large-crates-input"
                      value={formData.large_crates}
                      onChange={(e) => setFormData({ ...formData, large_crates: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="medium_crates">Medium (Crates)</Label>
                    <Input
                      id="medium_crates"
                      type="number"
                      data-testid="medium-crates-input"
                      value={formData.medium_crates}
                      onChange={(e) => setFormData({ ...formData, medium_crates: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="small_crates">Small (Crates)</Label>
                    <Input
                      id="small_crates"
                      type="number"
                      data-testid="small-crates-input"
                      value={formData.small_crates}
                      onChange={(e) => setFormData({ ...formData, small_crates: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="crack_crates">Cracks (Crates)</Label>
                    <Input
                      id="crack_crates"
                      type="number"
                      data-testid="crack-crates-input"
                      value={formData.crack_crates}
                      onChange={(e) => setFormData({ ...formData, crack_crates: e.target.value })}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any additional notes or special instructions..."
                  rows={3}
                  className="resize-none"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" data-testid="notification-submit-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {editingNotification ? 'Update' : 'Add Order'}
                </Button>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4 bg-gradient-to-br from-blue-50 to-white border-l-4 border-blue-500">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-600" />
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Orders</p>
          </div>
          <p className="text-3xl font-bold text-slate-900 mt-2">{notifications.length}</p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-amber-50 to-white border-l-4 border-amber-500">
          <div className="flex items-center gap-2">
            <Loader className="w-5 h-5 text-amber-600" />
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Pending</p>
          </div>
          <p className="text-3xl font-bold text-amber-600 mt-2">{pendingCount}</p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-500">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Completed</p>
          </div>
          <p className="text-3xl font-bold text-emerald-600 mt-2">{completedCount}</p>
        </div>
      </div>

      {/* Notifications Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Request #</th>
                <th>Customer</th>
                <th>Distributor</th>
                <th className="text-center">X-Large</th>
                <th className="text-center">Large</th>
                <th className="text-center">Medium</th>
                <th className="text-center">Small</th>
                <th className="text-center">Cracks</th>
                <th>Expected Pick-up Date</th>
                <th>Created By</th>
                <th>Status</th>
                {canManageOrders && <th>Actions</th>}
              </tr>
            </thead>
            <tbody data-testid="notifications-table-body">
              {notifications.map((notification) => {
                // Calculate crates from eggs (30 eggs per crate) for orders from approved requests
                const xlargeCrates = notification.xlarge_crates || Math.round((notification.xlarge_eggs || 0) / 30);
                const largeCrates = notification.large_crates || Math.round((notification.large_eggs || 0) / 30);
                const mediumCrates = notification.medium_crates || Math.round((notification.medium_eggs || 0) / 30);
                const smallCrates = notification.small_crates || Math.round((notification.small_eggs || 0) / 30);
                const crackCrates = notification.crack_crates || Math.round((notification.cracked_eggs || 0) / 30);
                
                return (
                <tr key={notification.id} className={notification.status === 'completed' ? 'bg-emerald-50' : ''}>
                  <td className="font-mono font-semibold text-blue-600">
                    {notification.source_request_number || notification.id?.substring(0, 8).toUpperCase() || '-'}
                  </td>
                  <td>
                    <div>
                      <p className="font-medium">{notification.customer_name}</p>
                    </div>
                  </td>
                  <td className="text-purple-600 font-medium">
                    {notification.distributor_name || <span className="text-slate-400">Direct to Farm</span>}
                  </td>
                  <td className="text-center font-semibold">{xlargeCrates}</td>
                  <td className="text-center font-semibold">{largeCrates}</td>
                  <td className="text-center font-semibold">{mediumCrates}</td>
                  <td className="text-center font-semibold">{smallCrates}</td>
                  <td className="text-center font-semibold">{crackCrates}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span>{formatDate(notification.pickup_date || notification.expected_delivery_date)}</span>
                      {notification.pickup_time && (
                        <span className="text-slate-500">at {notification.pickup_time}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        {/* Check if created_by looks like a UUID (old format) and use username instead */}
                        {(notification.created_by && notification.created_by.includes('-') && notification.created_by.length > 30)
                          ? (notification.created_by_username || 'Unknown')
                          : (notification.created_by || notification.created_by_username || '-')}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${
                      notification.status === 'completed' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {notification.status === 'completed' ? <CheckCircle className="w-3 h-3" /> : <Loader className="w-3 h-3" />}
                      {notification.status}
                    </span>
                  </td>
                  {canManageOrders && (
                    <td>
                      <div className="flex gap-1">
                        {notification.status === 'pending' && (
                          <button
                            data-testid={`fulfill-notification-${notification.id}`}
                            onClick={() => handleStatusChange(notification.id, 'completed')}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                            title="Mark as Fulfilled"
                          >
                            <Package className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          data-testid={`edit-notification-${notification.id}`}
                          onClick={() => handleEdit(notification)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-notification-${notification.id}`}
                          onClick={() => handleDelete(notification.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              )})}
            </tbody>
          </table>
          {notifications.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              {canManageOrders 
                ? 'No order notifications yet. Click "Add Order" to create one.'
                : 'No order notifications yet.'
              }
            </div>
          )}
        </div>
      </div>

      <div className="card p-4 bg-blue-50 border border-blue-200">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> Each order quantity is displayed in crates (1 crate = 30 eggs). 
          Update the Status to "Completed" once the order has been fulfilled.
        </p>
      </div>
    </div>
  );
};

export default OrderNotificationToFarm;
